<template>
  <div class="BaseTodoItem" @click="$emit('item-click')">
    <!-- Slot for Column 1: Checkbox -->
    <div class="BaseTodoItem__col BaseTodoItem__col--checkbox" @click.stop>
      <slot name="checkbox">
        <!-- Default content if needed -->
        <span class="mover"><i class="fa fa-ellipsis-v"></i></span>
        <span class="tick" :class="{'BaseTodoItem--completed': item.status === 'COMPLETED'}">
          <i class="fa fa-check-circle-o"></i>
        </span>
      </slot>
    </div>
    <!-- Slot for Column 2: Content -->
    <div class="BaseTodoItem__col BaseTodoItem__col--content">
      <slot name="content">
        {{ item.content }}
      </slot>
    </div>
    <!-- Slot for Column 3: Attachments -->
    <div class="BaseTodoItem__col BaseTodoItem__col--attachments">
      <slot name="attachments"></slot>
    </div>
    <!-- Slot for Column 3.5: Subtasks -->
    <div class="BaseTodoItem__col BaseTodoItem__col--subtasks">
      <slot name="subtasks"></slot>
    </div>
    <!-- Slot for Column 4: Comments -->
    <div class="BaseTodoItem__col BaseTodoItem__col--comments">
      <slot name="comments"></slot>
    </div>
    <!-- Slot for Column 5: Patients -->
    <div class="BaseTodoItem__col BaseTodoItem__col--patients">
      <slot name="patients"></slot>
    </div>
    <!-- Slot for Column 6: Status -->
    <div class="BaseTodoItem__col BaseTodoItem__col--status">
      <slot name="status"></slot>
    </div>
    <!-- Slot for Column 7: Tags -->
    <div class="BaseTodoItem__col BaseTodoItem__col--tags">
      <slot name="tags"></slot>
    </div>
    <!-- NEW: Slot for Column 7.5: Repeats -->
    <div class="BaseTodoItem__col BaseTodoItem__col--repeats">
      <slot name="repeats"></slot>
    </div>
    <!-- Slot for Column 8: Due Date -->
    <div class="BaseTodoItem__col BaseTodoItem__col--due-date">
      <slot name="due-date"></slot>
    </div>
    <!-- Slot for Column 9: Created By -->
    <div class="BaseTodoItem__col BaseTodoItem__col--creator">
      <slot name="creator"></slot>
    </div>
    <!-- Slot for Column 10: Assignees -->
    <div class="BaseTodoItem__col BaseTodoItem__col--assignees">
      <slot name="assignees"></slot>
    </div>
    <!-- Slot for Column 11: Actions -->
    <div class="BaseTodoItem__col BaseTodoItem__col--actions" @click.stop>
      <slot name="actions"></slot>
    </div>
    
  </div>
</template>

<script>
/**
 * @module BaseTodoItem
 */
export default {
  name: 'BaseTodoItem',
  props: {
    item: {
      type: Object,
      required: true
    }
  }
}
</script>
<style lang="scss" scoped>
.BaseTodoItem {
  /* This component's root element will have display: grid applied by TodoItem.vue */
  background: white;
  /* Padding might interfere with grid alignment, consider moving to TodoItem.vue or applying to inner slots */
  /* padding: .5rem; */ 
  border-radius: 5px;
  border-bottom: 1px solid #e8ecee;
  cursor: pointer; /* Add cursor pointer to indicate clickable */

  &--completed {
    text-decoration: line-through;
    opacity: .5;
  }

  &__pastdue {
    color: red;
  }

  /* Remove specific styles for __content and __actions as they are replaced by individual column slots/divs */
  /*
  &__content {
    flex: 1 1 auto; // Removed flex
    padding: 0 1rem; // Padding handled by grid gap or column styles in TodoItem.vue
    font-size: 14px;
    line-height: 1.5;
    font-weight:400;
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol";
    -webkit-font-smoothing: auto;    
    
    &:hover {
      cursor: pointer;
    }
  }
  */
  /*
  &__actions {
    display: flex; // Removed flex
    align-items: center;
    justify-content: center;
  }
  */

  /* Basic styling for the new column divs - can be overridden/enhanced in TodoItem.vue */
  &__col {
    display: flex; /* Use flex for alignment within each cell */
    align-items: center;
    overflow: hidden; /* Prevent content overflow */
    padding: 0 4px; /* Minimal padding within cells */
  }

  &__col--checkbox { justify-content: center; }
  &__col--content { justify-content: flex-start; padding-left: 0.25rem; } /* Align text left */
  /* Icon columns need overflow: visible for count badges */
  &__col--attachments { justify-content: center; overflow: visible; }
  &__col--subtasks { justify-content: center; overflow: visible; }
  &__col--comments { justify-content: center; overflow: visible; }
  &__col--patients { justify-content: center; }
  &__col--tags { justify-content: flex-start; } /* Align tags left */
  &__col--status { justify-content: center; overflow: visible; }
  &__col--repeats { justify-content: flex-start; } /* NEW: Basic style for Repeats column */
  &__col--due-date { justify-content: center; }
  &__col--creator { justify-content: flex-start; padding-left: 0.25rem; } /* Align creator left */
  &__col--assignees { justify-content: flex-start; padding-left: 0.25rem; } /* Align assignees left for consistent column alignment */
  &__col--actions { justify-content: flex-end; } /* Align actions right */

  /* ============================================ */
  /* Responsive column hiding — must be here (scoped) */
  /* to override the scoped display: flex above */
  /* ============================================ */

  /* Hide Comments at <= 1200px */
  @media (max-width: 1200px) {
    &__col--comments { display: none !important; }
  }

  /* ============================================ */
  /* SHARED CARD LAYOUT for tablet + phone (≤1024px) */
  /* 4-col grid: checkbox | left | right | delete */
  /* Items pair up: status+date, patient+tags, creator+assignees */
  /* ============================================ */
  @media (max-width: 1024px) {
    &__col { padding: 0 2px; min-height: 0; overflow: visible !important; }

    &__col--attachments,
    &__col--subtasks,
    &__col--comments,
    &__col--patients,
    &__col--tags,
    &__col--repeats,
    &__col--due-date,
    &__col--creator,
    &__col--assignees { display: flex !important; }

    /* Row 1: Checkbox | Task text (spans left+right) | Delete */
    &__col--checkbox  { grid-row: 1; grid-column: 1; align-self: start; padding-top: 2px; }
    &__col--content   { grid-row: 1; grid-column: 2 / 4; }
    &__col--actions   { grid-row: 1; grid-column: 4; align-self: start; }

    /* Row 2: Status (left) | Date (right) */
    &__col--status    { grid-row: 2; grid-column: 2; justify-content: flex-start; }
    &__col--due-date  { grid-row: 2; grid-column: 3; justify-content: flex-end; }

    /* Row 3: Patient (left) | Tags + Repeats (right) */
    &__col--patients  { grid-row: 3; grid-column: 2; justify-content: flex-start; }
    &__col--tags      { grid-row: 3; grid-column: 3; justify-content: flex-end; }
    &__col--repeats   { grid-row: 4; grid-column: 2; justify-content: flex-start; }

    /* Row 4: Icons (all left side) */
    &__col--attachments { grid-row: 4; grid-column: 3; justify-content: flex-end; }
    &__col--subtasks    { grid-row: 5; grid-column: 2; }
    &__col--comments    { grid-row: 5; grid-column: 3; }

    /* Row 5: Creator (left) | Assignees (right) */
    &__col--creator   { grid-row: 6; grid-column: 2; justify-content: flex-start; }
    &__col--assignees { grid-row: 6; grid-column: 3; justify-content: flex-end; }
  }

}
</style>
